import { PDFDocument, PDFForm, rgb } from 'pdf-lib';
import { SubscriptionAgreementFieldName } from 'src/constants';
import {
  AccountDto,
  AccountHolderDto,
  AccountHolderPhysicalAddressDto,
  AccountTypeDto,
  EntityAccountTypeDto,
  EntityInformationDto,
  PhoneNumberTypeDto,
  RetirementAccountTypeDto,
} from 'src/dtos';

import { getDecimalFormatter } from './getDecimalFormatter';
import { Logger } from './Logger';

// Ports worker/src/modules/offer-order/fillSubscriptionAgreement.ts (the code that fills real
// subscription agreements in production) so an admin can preview a newly uploaded agreement
// before it goes live. The field-mapping rules below (mapEntityAccountFormFields /
// mapNonEntityAccountFormFields / getAccountHolderFullName / getInvestorName /
// getEntityAccountHolderTitle) are copied verbatim from that worker module - only the input
// (admin's own AccountDto/AccountHolderDto/EntityInformationDto filled with fake data, instead of
// a real record pulled from Prisma) and the "fill" step (raw pdf-lib here instead of worker's
// PdfFormEditor/S3/EncipherService) differ. Admin's DTOs name a couple of fields differently than
// worker's Prisma-derived types (e.g. `state`/`country` here vs `stateAbbreviation`/
// `countryAbbreviation` there, `entityInformation` here vs `entity` there) - the mapping below
// follows admin's naming.

type FormField =
  | { type: 'text'; name: SubscriptionAgreementFieldName; value: string | number }
  | { type: 'checkbox'; name: SubscriptionAgreementFieldName; value: boolean }
  | { type: 'signature'; name: SubscriptionAgreementFieldName; value: string };

interface OfferOrderPreviewInput {
  quantity: number;
  totalCost: number;
  localeDate: string;
  account: AccountDto;
}

// No currency symbol - the sub agreement's PDF template already prints the "$" next to this field.
const decimalFormatter = getDecimalFormatter();

// Must match the copy on the real PDF exactly, hence a private map rather than reusing
// admin's own RetirementAccountTypeLabel (that one is UI display text, not agreement text).
// In the future re-use the RetirementAccountTypeLabel enum from models (but first remove the cyclical dependency
// with Maybe util).
const RETIREMENT_ACCOUNT_TYPE_PDF_LABEL: Record<RetirementAccountTypeDto, string> = {
  [RetirementAccountTypeDto.Traditional]: 'Traditional IRA',
  [RetirementAccountTypeDto.Rollover]: 'IRA Rollover',
  [RetirementAccountTypeDto.Roth]: 'Roth IRA',
  [RetirementAccountTypeDto.SEP]: 'SEP IRA',
  [RetirementAccountTypeDto.Simple]: 'Simple IRA',
  [RetirementAccountTypeDto.Beneficiary]: 'Beneficiary IRA',
  [RetirementAccountTypeDto.CoverDell]: 'Coverdell IRA',
  [RetirementAccountTypeDto.RothBeneficiary]: 'Roth Beneficiary IRA',
};

const getEntityAccountHolderTitle = (entityAccountType?: EntityAccountTypeDto): string => {
  switch (entityAccountType) {
    case EntityAccountTypeDto.CCorporation:
    case EntityAccountTypeDto.SCorporation:
      return 'Officer/Beneficial Owner';
    case EntityAccountTypeDto.LlcCCorporation:
    case EntityAccountTypeDto.LlcSCorporation:
    case EntityAccountTypeDto.LlcPartnership:
      return 'Manager/Member/Beneficial Owner';
    case EntityAccountTypeDto.PersonalTrust:
    case EntityAccountTypeDto.BusinessTrust:
      return 'Trustee';
    case EntityAccountTypeDto.Partnership:
      return 'Partner';
    default:
      return '';
  }
};

const getAccountHolderFullName = ({
  firstName,
  middleName,
  lastName,
  suffix,
  retirementAccountType,
}: {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
  retirementAccountType?: RetirementAccountTypeDto;
}): string => {
  const fullName = `${firstName}${middleName ? ' '.concat(middleName) : ''} ${lastName}${
    suffix ? ' '.concat(suffix) : ''
  }`;

  if (retirementAccountType) {
    return `${fullName} ${RETIREMENT_ACCOUNT_TYPE_PDF_LABEL[retirementAccountType]}`;
  }

  return fullName;
};

const getInvestorName = (account: AccountDto): string => {
  const { primaryAccountHolder, secondaryAccountHolder } = account;

  if (!primaryAccountHolder) {
    return '';
  }

  const { firstName, middleName, lastName, suffix } = primaryAccountHolder;
  const fullName = [firstName, middleName, lastName, suffix].filter(Boolean).join(' ');

  if (account.type === AccountTypeDto.Retirement && account.retirementAccountType) {
    return `${fullName} ${RETIREMENT_ACCOUNT_TYPE_PDF_LABEL[account.retirementAccountType]}`;
  }

  if (account.type === AccountTypeDto.Ugma && secondaryAccountHolder) {
    const jointFullName = [
      secondaryAccountHolder.firstName,
      secondaryAccountHolder.middleName,
      secondaryAccountHolder.lastName,
      secondaryAccountHolder.suffix,
    ]
      .filter(Boolean)
      .join(' ');

    return `${fullName} C/F ${jointFullName}`;
  }

  if (account.type === AccountTypeDto.Joint && secondaryAccountHolder) {
    const jointFullName = [
      secondaryAccountHolder.firstName,
      secondaryAccountHolder.middleName,
      secondaryAccountHolder.lastName,
      secondaryAccountHolder.suffix,
    ]
      .filter(Boolean)
      .join(' ');

    return `${fullName} and ${jointFullName}`;
  }

  return fullName;
};

const mapEntityAccountFormFields = (offerOrder: OfferOrderPreviewInput): FormField[] => {
  const { totalCost, quantity, localeDate, account } = offerOrder;
  const { entityInformation, primaryAccountHolder, secondaryAccountHolder, entityAccountType } = account;

  if (!entityInformation) {
    throw new Error('Preview entity account is missing entity information');
  }

  const isEntityTrustAccount =
    entityAccountType === EntityAccountTypeDto.PersonalTrust ||
    entityAccountType === EntityAccountTypeDto.BusinessTrust;
  const isFinraMember = Boolean(
    primaryAccountHolder?.disclosure?.industryEmployed || secondaryAccountHolder?.disclosure?.industryEmployed,
  );
  const title = getEntityAccountHolderTitle(entityAccountType);

  const fields: FormField[] = [
    { type: 'text', name: SubscriptionAgreementFieldName.Quantity, value: quantity },
    { type: 'text', name: SubscriptionAgreementFieldName.TotalCost, value: decimalFormatter.format(totalCost) },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.Individual, value: false },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.Joint, value: false },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.Entity, value: true },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.Retirement, value: false },
    { type: 'text', name: SubscriptionAgreementFieldName.PrimaryHolderName, value: entityInformation.name },
    { type: 'text', name: SubscriptionAgreementFieldName.PrimaryHolderSsid, value: entityInformation.taxId },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderAddress1,
      value: [entityInformation.address.address1, entityInformation.address.address2].filter(Boolean).join(', '),
    },
    { type: 'text', name: SubscriptionAgreementFieldName.PrimaryHolderCity, value: entityInformation.address.city },
    { type: 'text', name: SubscriptionAgreementFieldName.PrimaryHolderState, value: entityInformation.address.state },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderPostalCode,
      value: entityInformation.address.postalCode,
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderCountry,
      value: entityInformation.address.country,
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderPhoneNumber,
      value: entityInformation.phoneNumber ?? '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderEmail,
      value: primaryAccountHolder?.email ?? '',
    },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.NonAccredited, value: !account.isAccredited },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.Accredited, value: Boolean(account.isAccredited) },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.FinraMember, value: isFinraMember },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.NonFinraMember, value: !isFinraMember },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryInvestorName,
      value: getAccountHolderFullName({
        firstName: primaryAccountHolder?.firstName,
        middleName: primaryAccountHolder?.middleName,
        lastName: primaryAccountHolder?.lastName,
        suffix: primaryAccountHolder?.suffix,
      }),
    },
    { type: 'text', name: SubscriptionAgreementFieldName.PrimaryInvestorTitle, value: title },
    { type: 'text', name: SubscriptionAgreementFieldName.PrimaryInvestorSignatureDate, value: localeDate },
  ];

  if (primaryAccountHolder) {
    fields.push({
      type: 'signature',
      name: SubscriptionAgreementFieldName.PrimaryHolderSignature,
      value: johnDoeSignature,
    });
  }

  if (isEntityTrustAccount && secondaryAccountHolder) {
    fields.push({
      type: 'text',
      name: SubscriptionAgreementFieldName.JointInvestorName,
      value: getAccountHolderFullName({
        firstName: secondaryAccountHolder.firstName,
        middleName: secondaryAccountHolder.middleName,
        lastName: secondaryAccountHolder.lastName,
        suffix: secondaryAccountHolder.suffix,
      }),
    });
    fields.push({
      type: 'signature',
      name: SubscriptionAgreementFieldName.JointHolderSignature,
      value: janeDoeSignature,
    });
    fields.push({ type: 'text', name: SubscriptionAgreementFieldName.JointInvestorTitle, value: title });
    fields.push({ type: 'text', name: SubscriptionAgreementFieldName.JointInvestorSignatureDate, value: localeDate });
  }

  return fields;
};

const mapNonEntityAccountFormFields = (offerOrder: OfferOrderPreviewInput): FormField[] => {
  const { totalCost, quantity, localeDate, account } = offerOrder;
  const { primaryAccountHolder, secondaryAccountHolder } = account;

  const isJointOrUgmaAccount = account.type === AccountTypeDto.Joint || account.type === AccountTypeDto.Ugma;
  const isFinraMember = Boolean(
    primaryAccountHolder?.disclosure?.industryEmployed || secondaryAccountHolder?.disclosure?.industryEmployed,
  );

  const primaryPhoneNumber = primaryAccountHolder?.phones?.[0]?.phoneNumber ?? '';
  const secondaryPhoneNumber = secondaryAccountHolder?.phones?.[0]?.phoneNumber ?? '';

  const fields: FormField[] = [
    { type: 'text', name: SubscriptionAgreementFieldName.Quantity, value: quantity },
    { type: 'text', name: SubscriptionAgreementFieldName.TotalCost, value: decimalFormatter.format(totalCost) },
    {
      type: 'checkbox',
      name: SubscriptionAgreementFieldName.Individual,
      value: account.type === AccountTypeDto.Individual,
    },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.Joint, value: isJointOrUgmaAccount },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.Entity, value: false },
    {
      type: 'checkbox',
      name: SubscriptionAgreementFieldName.Retirement,
      value: account.type === AccountTypeDto.Retirement,
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderName,
      value: getAccountHolderFullName({
        firstName: primaryAccountHolder?.firstName,
        middleName: primaryAccountHolder?.middleName,
        lastName: primaryAccountHolder?.lastName,
        suffix: primaryAccountHolder?.suffix,
        retirementAccountType: account.type === AccountTypeDto.Retirement ? account.retirementAccountType : undefined,
      }),
    },
    { type: 'text', name: SubscriptionAgreementFieldName.PrimaryHolderSsid, value: primaryAccountHolder?.ssid ?? '' },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderAddress1,
      value: primaryAccountHolder?.physicalAddress
        ? [primaryAccountHolder.physicalAddress.address1, primaryAccountHolder.physicalAddress.address2]
            .filter(Boolean)
            .join(', ')
        : '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderCity,
      value: primaryAccountHolder?.physicalAddress?.city ?? '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderState,
      value: primaryAccountHolder?.physicalAddress?.state ?? '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderPostalCode,
      value: primaryAccountHolder?.physicalAddress?.postalCode ?? '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderCountry,
      value: primaryAccountHolder?.physicalAddress?.country ?? '',
    },
    { type: 'text', name: SubscriptionAgreementFieldName.PrimaryHolderPhoneNumber, value: primaryPhoneNumber },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.PrimaryHolderEmail,
      value: primaryAccountHolder?.email ?? '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.JointHolderName,
      value:
        isJointOrUgmaAccount && secondaryAccountHolder
          ? getAccountHolderFullName({
              firstName: secondaryAccountHolder.firstName,
              middleName: secondaryAccountHolder.middleName,
              lastName: secondaryAccountHolder.lastName,
              suffix: secondaryAccountHolder.suffix,
            })
          : '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.JointHolderSsid,
      value: isJointOrUgmaAccount ? secondaryAccountHolder?.ssid ?? '' : '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.JointHolderAddress1,
      value:
        isJointOrUgmaAccount && secondaryAccountHolder?.physicalAddress
          ? [secondaryAccountHolder.physicalAddress.address1, secondaryAccountHolder.physicalAddress.address2]
              .filter(Boolean)
              .join(', ')
          : '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.JointHolderCity,
      value: isJointOrUgmaAccount ? secondaryAccountHolder?.physicalAddress?.city ?? '' : '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.JointHolderState,
      value: isJointOrUgmaAccount ? secondaryAccountHolder?.physicalAddress?.state ?? '' : '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.JointHolderPostalCode,
      value: isJointOrUgmaAccount ? secondaryAccountHolder?.physicalAddress?.postalCode ?? '' : '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.JointHolderCountry,
      value: isJointOrUgmaAccount ? secondaryAccountHolder?.physicalAddress?.country ?? '' : '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.JointHolderPhoneNumber,
      value: isJointOrUgmaAccount ? secondaryPhoneNumber : '',
    },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.JointHolderEmail,
      value: isJointOrUgmaAccount ? secondaryAccountHolder?.email ?? '' : '',
    },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.NonAccredited, value: !account.isAccredited },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.Accredited, value: Boolean(account.isAccredited) },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.FinraMember, value: isFinraMember },
    { type: 'checkbox', name: SubscriptionAgreementFieldName.NonFinraMember, value: !isFinraMember },
    { type: 'text', name: SubscriptionAgreementFieldName.PrimaryInvestorName, value: getInvestorName(account) },
    { type: 'text', name: SubscriptionAgreementFieldName.PrimaryInvestorSignatureDate, value: localeDate },
    {
      type: 'text',
      name: SubscriptionAgreementFieldName.JointInvestorSignatureDate,
      value: account.type === AccountTypeDto.Joint ? localeDate : '',
    },
  ];

  if (primaryAccountHolder) {
    fields.push({
      type: 'signature',
      name: SubscriptionAgreementFieldName.PrimaryHolderSignature,
      value: johnDoeSignature,
    });
  }

  if (account.type === AccountTypeDto.Joint && secondaryAccountHolder) {
    fields.push({
      type: 'signature',
      name: SubscriptionAgreementFieldName.JointHolderSignature,
      value: janeDoeSignature,
    });
  }

  return fields;
};

const todayLocaleDate = (): string => new Date().toLocaleDateString('en-US');

const buildAddress = (overrides: Partial<AccountHolderPhysicalAddressDto> = {}): AccountHolderPhysicalAddressDto => ({
  address1: '123 Main Street',
  address2: 'Suite 100',
  city: 'San Francisco',
  state: 'CA',
  postalCode: '94102',
  country: 'US',
  ...overrides,
});

const johnDoeFixture: AccountHolderDto = {
  id: 'preview-primary-account-holder',
  firstName: 'John',
  lastName: 'Doe',
  email: 'john.doe@example.com',
  ssid: '123-45-6789',
  phones: [{ phoneNumber: '(415) 555-0123', type: PhoneNumberTypeDto.Mobile }],
  physicalAddress: buildAddress(),
};

const janeDoeFixture: AccountHolderDto = {
  id: 'preview-secondary-account-holder',
  firstName: 'Jane',
  lastName: 'Doe',
  email: 'jane.doe@example.com',
  ssid: '987-65-4321',
  phones: [{ phoneNumber: '(415) 555-0124', type: PhoneNumberTypeDto.Mobile }],
  physicalAddress: buildAddress(),
};

const entityInformationFixture: EntityInformationDto = {
  name: 'Acme Investment Holdings, LLC',
  taxId: '12-3456789',
  phoneNumber: '(415) 555-0199',
  address: buildAddress({ address1: '400 Market Street', address2: 'Floor 12' }),
  createdAt: new Date().toISOString(),
};

const johnDoeSignature = [
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAApgAAAAoCAYAAABaZIqVAAAAAXNSR0IArs4c6QAADShJREFUeF7tnX',
  'WIVc8bxl+7W0wsVGyxMbAbO7C7Fbu7W1zs7lZs3bUTuwMLxe5W7PzxDMxh7tl7d8/xt192rz4vXHTvmZk787n3j4e3JtLv379/C4',
  '0ESIAESIAESIAESIAEwohAJArMMCLJZUiABEiABEiABEiABBQBCkz+EEiABEiABEiABEiABMKUAAVmmOLkYiRAAiRAAiRAAiRAAh',
  'SY/A2QAAmQAAmQAAmQAAmEKQEKzDDFycVIgARIgARIgARIgAQihMC8fv26bN68Wc6fPy9lypSRtm3bSuTIkfntkAAJkAAJkAAJkA',
  'AJ+CGBcBeYFy5ckPr163ugCwgIkCpVqvghTm6ZBEiABEiABEiABEjAp8D88uWLXL16VV69eiVv3ryR169fy9u3byVKlCiSIkUK9U',
  'qZMqX6N0mSJBIpUiTXNF+8eCE1atRQn2FarVq1ZPz48a7XczPh/fv3cuTIEbl//7563blzR+7duyeJEiWS5MmTS9q0aaVUqVJSuH',
  'BhiRkzppulOZYESIAESIAESIAE/mkCHgITPdePHj0qQUFBsmHDBsdg0qRJIxs3bpT48eM7noOB48aNkyVLlqg5s2fPlrlz5wo8mh',
  'Csx44dc7WW08EQkatWrbI+N7R5ceLEkb59+0q9evUYtg8NFp+TAAmQAAmQAAmQgNkH89evXzJ27FhZvnz5H4EZPny4NGzY0PHcDx',
  '8+SP78+dX49OnTy65du6Rz586yZ88e62/HizkY+PnzZxk0aJAEBgY6GB18CLyZM2bMkGjRov3RfE4iARIgARIgARIggX+FgOXBHD',
  'VqlKxYscLj3Ah/582bVxImTChx48ZVHryPHz/KxYsX5fLly9ZYeBznz58vOXLkcMxt9erVAlEK6969u3Ts2FGqV68uN27ckLJly8',
  'qsWbMcr+VkILyWI0aM8BiaL18+KVCggGTIkEG9EidOLE+ePFHhcnhW79696zG+UaNGMmzYMCcfxzEkQAIkQAIkQAIk8M8SUAIT3r',
  '08efJYEOCtGzp0qKROndorGISzp0yZYj3bvn27ZM6c2RXEpk2byqlTp9ScdevWqXzO4sWLewhOVwuGMnjhwoUyceJENap8+fLSpk',
  '0bjzPbp//48UOWLl1qzdHP16xZo0Q3jQRIgARIgARIgARIwDsBJTBPnz4tTZo0USOQc4i/Uczjy4YMGaJEobYzZ85IvHjxHDO2C1',
  'oUE0HMTZgwQa1x+PBhVWgTloaiHoTHUbQDbyVaI82ZM0cePnwoxYoVkx49enj9OITV169fbz2rXbu2yh2lkQAJkAAJkAAJkAAJhC',
  'Awt23bJr1791YjdD5kSMBatWqlioG0ID137pwrvvBcwoMJy5Url8ybN0/KlSunwu+VKlWSqVOnulrvTwabHlTMh+D0VgkPYVqwYE',
  'GPj7h27RoLfv4EOueQAAmQAAmQAAn8EwSUBxNtehA21oZCG7Tp8WUlS5aUp0+fqsfIY0Q+pRtDfqUWkSgMgrDcunWrWmL37t2SLl',
  '06N8u5Hvv9+3fJmTOnNa9EiRIqh9SX1a1b1yPn9Pjx4ypfk0YCJEACJEACJEACJBCcgFXkY4rGtWvX+sxPRG6iWcyDJukjR450xX',
  'bgwIFWG6RChQpZuZh4v3nz5q7W+pPBV65cEYS6tSHkr1MEvK03YMAA1YZJ25YtWyRr1qx/8tGcQwIkQAIkQAIkQAJ/PQFLYKI/JA',
  'pYUFWNKm5f9uDBAxXO1oYcxWbNmll///z5U06ePKmaliOfEyFw5Dya1rp1a9Xk3DR4QhctWiRnz56Vd+/eCbyKZl4nQtioXM+ePb',
  'slcD99+qT6ZaK6HXmUMWLEcPSF2SvK4T3NkiWLz7l2gWkf7+TMenHkn+KMjx49UtX5RYoUcd0/1NEhOYgESIAESIAESIAEwomA46',
  'siIaL27dun8iXNFkULFiywqr9v3rypqrN1+FyfqVOnTtK1a1crxxHXQN66dcs6MtocjR49WuWBIlwOQ1h6zJgx6v8nTpywPJt16t',
  'RR/ToPHjwo7du3t9bo06eP+mwnhrE6JI/xKDIKqagJYXwzzxSiFnuGOT0zxqJ4CS2Z9BnxHkQ4PMZuq/CdnJNjSIAESIAESIAESC',
  'A8CIQqML9+/arCw2jzA++l3Q4cOCCpUqWSly9fCsSfXVzq8agQr1mzpvoT3kpTZKEl0uTJkz3eQxheh6Uhynbs2KHmwmOaLFky6d',
  'atm8dWKleu7NE6KSSYZjoAQvShNZe371cX+bg5M0QsrsD0Zv9F38/w+DHxM0mABEiABEiABEgABEIUmPAy9uzZUzU/92bmlY6mlw',
  '/vQ7hpUYi5eqy9RVFAQIDyVNrvIy9Tpoy6PhJWtGhR6/m0adOUN9RuCNNDfIZmz549U+F3bfB6wqPpy3APO1obaTOr7J2eGZXoqI',
  '7XZ0TDdvQdxRWUMDS0P3ToUGhb53MSIAESIAESIAES8AsCXgUm7iRHPqa+aUefpGXLlrJ48WLrYLh5Z9KkScp7iRxIbehpifxJs5',
  'AGz9B3EhXrEHRRo0ZVLZEg8ODdRKgYpj2bCH9D3NpzPuEF3LRpkxJlprfUngvqiz7C1G3btrUe4wYj3DPuy6ZPn66uiNQGzylC/m',
  '7OjJZOEMY4M3JIUSmPqzJ1+yPkqZq9Nv3il8NNkgAJkAAJkAAJkIAPAl4FpnnrDebBG6mboJcuXdpaCtXjqCK3i7ZLly6pwhuzFZ',
  'CehJA7rmNEzmOaNGmUsISohPjE68KFC2oo2hjB62c2gTfPgBxK3eII78PbCa9naLZs2TIrtxNjIYZND6U53y4i8Uy3cHJzZnhBUb',
  'iEM8Nr2bhxY9WsXfcS7dKli7qHnUYCJEACJEACJEACfwOBYAIT1d2o8tYGETd48GDlfTMbsuM5wrrwJM6cOVN56GBmX0yIMbtwQo',
  'gcYerYsWOrXEotYHH3N7x72nbu3Kmqz7EGWhdBnGEODAVA2JN5vaXT6yrhlTX7du7fv9/rlZgoakJuqOlZNMP2bs4MQQ1hjf3jTG',
  'b+KXJNUdUeM2bMv+H3xDOQAAmQAAmQAAmQgGcOJkQV7gPXuYLw7CEkDm8kDN439ICEQdyh+hk2d+5cQS4lzMzLtN+Cg5A5PgPz0F',
  'JI35yzcuVKJVTN9ki6shu3BuF5rFixrOsjUVgEkVa1alXrK3R6XaX9Bh9vFeTPnz9XYXxUr2vDueA1TZo0qeszQ4ijLZF5ZizSq1',
  'cvadGihaASH62L4DmmkQAJkAAJkAAJkIC/E/DwYF68eNEjH9G8VQeiC+JT24gRI6RBgwbqT7TtQX6mNngjEVbHjT06fxE5muhzaf',
  'dqQrjBa4remRBbMITNIe4gvFAAhFA1ekbihT6dEJz2NkVOr29ED09dDW8W7OB8t2/fVldGwhtrehmxJwhtFBtpc3Nmu9cU4fEKFS',
  'pI3LhxVSoAOMHQAB6eYhoJkAAJkAAJkAAJ+DMBD4Fphn1NTyQOaAohCEDkD8KrCIOnslSpUsH6O5oiDfmVyN9E6NtsMaSruM37yU',
  '2gWAM5jPBwQnxpYbthwwbBzT/afFW6m2uheMl+Aw/O+eXLl2CCUs/D5+LsZjje7ZntaQcQtgiNI1dVi114i5EPSiMBEiABEiABEi',
  'ABfyfgITAh3pAPCDNb56DJOHIxtaEhulmJjfftIsoEA+9m//791VsIb3fo0MF6HBQUJBkzZlQ5irlz5w7GEz02IcJ0I3R9DzhyI3',
  'VbIhQL7d27N9TvAgIzf/78PsWkfQHkevbr18/nTTtOz4x1UREfGBjodY/YPzyk+JdGAiRAAiRAAiRAAv5OwENgwoOGW3K0IWSdIE',
  'ECFarW3khcqQjvYbRo0YKdHQUzaI6OMDgMggk9K6tVq2blWz5+/FgV6cBr2K5dOw+xiXVRDQ5BCS8pRC2EbMeOHdVNOtgPrm2EYQ',
  'wq2JEv2qNHD491QvpSIEwh5lB4Yw+Dw7OIV8WKFaV8+fIeV1X6WtPJmTEXuacQ7ytWrJC7d++q5TJlyqRaFaEQSud2+vsPivsnAR',
  'IgARIgARIgAQ+BifvDkRvoyxBORv6j/W5x+/hv374JXsgx9GbwVqLAJ3r06MEew8v49u1blW+pi4DwHgSpDsnrSRBteF/30HT7df',
  '748UNwnzksfvz4bqd7jA/tzOZg7Bmf7YvP/7URTiYBEiABEiABEiCBcCYQrE0RwrgI59oNOYhoqp42bdpw3jI/ngRIgARIgARIgA',
  'RIICIT8NpoHRXVqOJGv0q01smWLZt160xEPgz3RgIkQAIkQAIkQAIkEP4EQryLPPy3xx2QAAmQAAmQAAmQAAn4GwEKTH/7xrhfEi',
  'ABEiABEiABEojgBCgwI/gXxO2RAAmQAAmQAAmQgL8RoMD0t2+M+yUBEiABEiABEiCBCE6AAjOCf0HcHgmQAAmQAAmQAAn4GwEKTH',
  '/7xrhfEiABEiABEiABEojgBCgwI/gXxO2RAAmQAAmQAAmQgL8RoMD0t2+M+yUBEiABEiABEiCBCE7gf48c7tT/xMlsAAAAAElFTk',
  'SuQmCC',
].join('');

const janeDoeSignature = [
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAApgAAAAoCAYAAABaZIqVAAAAAXNSR0IArs4c6QAADKdJREFUeF7tnX',
  'dsTe8fxz9Gvyhqq1kEETNqj4jWHiFmzdgjsWuLTey991a7topVI0bF3gRNETNU7ZLil/eTPCfn3t7bXvfbX532+/4kor3nnOd8nt',
  'ftH+981pPi9+/fv4VGAiRAAiRAAiRAAiRAAglEIAUFZgKR5DIkQAIkQAIkQAIkQAKKAAUm/xBIgARIgARIgARIgAQSlAAFZoLi5G',
  'IkQAIkQAIkQAIkQAIUmPwbIAESIAESIAESIAESSFACFJgJipOLkQAJkAAJkAAJkAAJUGDyb4AESIAESIAESIAESCBBCVBgJihOLk',
  'YCJEACJEACJEACJOBUYEZHR8vdu3fl3bt38v79e4mMjJSoqChJlSqV5MqVS/3LnTu3+j9btmySIkWKJEEzJiZGzp8/L+Hh4fLs2T',
  'OJiIiQR48eiYeHh3h7e6s9VatWTfz9/SVLlixJYk90kgRIgARIgARIgASsRMBGYGLm+rlz5yQkJESCg4Nd9jN//vyye/du8fLycv',
  'mZxL7x48ePak+rVq1SotkV69SpkwQGBoqnp6crt/MeEiABEiABEiABEiAB8xzMX79+ydSpU2XTpk1ugZkwYYK0a9fOrWf/3w8tX7',
  '5c5s2b59ZrEKHdunWr5MmTx63n+RAJkAAJkAAJkAAJ/NcIGBHMyZMny+bNm232D3Hl6+srmTNnlgwZMkjKlCnly5cvcuPGDbl165',
  'ZxL1LkiAyWLFnScvwePHggTZs2tfELEVc/Pz8pXLiwFCxYUIlHlAA8ffpURW9PnTplcz/2FRQUJOnSpbPc/ugQCZAACZAACZAACV',
  'iNgBKY3759k7Jlyxq+QXyNGzdO8ubN69DfZcuWyfz5841rBw8elKJFi1ptb8ofiOGAgAD1M4Ri7969pU6dOqqW1JlBYA4ePFiJaW',
  '1DhgyRXr16WXKPdIoESIAESIAESIAErERACcxLly5Jx44dlV/p06cX/B6XABs7dqzs2LHD2Mfly5clY8aMVtqX4cvPnz/lwIEDKk',
  'pZsWJF+fTpk8ydO1fu3bunGpSmT5/uMDK5f/9+GTZsmLEO7g0NDY2TiyUB0CkSIAESIAESIAESSGQCSmBCgA0dOlS9GinjI0eOxO',
  'lGt27dVDOQFqRXr15NZLfdf93SpUtlwYIFxgJ79uyREiVKxFoQDU/t27cX897QJFSqVCn3X84nSYAESIAESIAESOA/QEAJTNQe1q',
  '1b19jusWPHxMfHx+n2a9asKa9evVLXy5Urp5pgkop1795dzp49a7h7+/ZtNaLIkc2ZM0dWrlxpXFqyZIlKr9NIgARIgARIgARIgA',
  'ScEzCafMyicfv27TY1mebHMUfS3MzTpk0bmTRpUpJgjHS5OVoJUb148WKnvu/du1dGjBhhXEddaocOHZLEXukkCZAACZAACZAACf',
  'wtAobAfPLkiWzbtk0qVKggtWvXduoPhpObo3ijR48WzIs024sXL+TatWvy8uVLadiwocNmofv376tOdAg+LVi/fv2qhqBjaHv16t',
  'Ulbdq0NuuiGenKlSvy/Plz1dletWrVP5q9ad9RPn78eJUGd2bxCUwI1osXLwrYoXa1dOnSUqhQIYfL/Vvf/9YfCN9LAiRAAiRAAi',
  'RAAn9KwOWjIiGmTpw4oVLG5hFFq1evlho1aqj34p5p06bZzNKEeCxWrJg0adJEnZADCwsLk86dO6ufW7ZsqeZvonMbHd7a7Lu2z5',
  'w5I4MGDbLp7IaoQ7TV1Q72Xbt2CQSxtp07d0qZMmWcMlu0aJFNhBPRTl1K8PDhQ+nRo4dRKqAX6dOnjwwYMMDmZKOE8P1Pv1jeTw',
  'IkQAIkQAIkQAJ/i0C8AvP79+/qlJ41a9aooxXt7eTJk8YQcgxbd1aPCaGJdWAQiocPH1Y/Q/DlzJlTBg4caLM0BOns2bPVZziysn',
  'nz5g4ZIdqKxh1XDO+CyNR28+ZNSZMmjdNH0fiEBihtunTg7du3ShjrOlT7BWbMmCHNmjVLUN9d2R/vIQESIAESIAESIAErEIhTYO',
  'KMbsyDRGrZkWHAOlLasHXr1qmRP9q6dOmizjDft2+f+qhWrVqC+ZkwRDL1cY0LFy5UET9769q1q4wcOVJwxGODBg2M+5HSxszO4c',
  'OHq0cwPuj06dMusURqX4tks+B19nDjxo3VOeXa0ByUI0cOdWKR7i4Hg0qVKhmCGfdqLgnpu0sb5E0kQAIkQAIkQAIkYAECDgUmRv',
  'SgHhMRSbNB9EFIasMJObNmzZI7d+5IixYtjM979uypxh5BnB46dEh9jtQxopT2NZyITGJUEISiOSKItDmihMePH5e+ffuqNXACz9',
  'GjR+Xz589qpiUMdY/mqKQzpnimfPnyxmVzhNTRMxcuXBCIZG0QkThGE9FL1Idq27Bhg5oBat4/ruF4SoxygoBOnTq1qjV113cL/J',
  '3QBRIgARIgARIgARJwmYBDgYl0+MyZM23EFdK+MH9/f+NzdI+ji9x8zCTmaCKt/M8//4i5Mx3DzRERNA91N3uJSCeik/r0HKTTEW',
  'VEmhzHUMIQOUQXN+o89RzO/v37S79+/eLdsL0Ijus5COzWrVvb1JrqtDfqKSGgtSHNjiM0Hc3HRAT3w4cPajg7Iq7u+h7v5ngDCZ',
  'AACZAACZAACViIQCyBiTQwZkVqg6gbM2aMisKZB7LjOlLTiDyaU886ehkVFSWVK1c21sHJOGj2wYxNNPhgPX22d6tWrVR0UzcL4S',
  'Ec8YgucghYvAc/Z82a1abJBwJ0y5YtsbrNHfFFJBURVW2IvNqfUa6vQdyOGjXKuBcpb0QfcR47ZmEiKgkzzwDFvuyFLrroUcPq6e',
  'mpusvNR0/+ie8W+nuhKyRAAiRAAiRAAiQQLwEbgYkucIg8XR9ZpUoVlRJHhA6GKJyuqUQdJJpe7GdLzps3Txo1aiQhISESGBhoOH',
  'D9+nUlKJF2RiMQfvb29lbX0SiEUT86JV2kSBGVWsfa6PJGHaSXl5cSmNrQZY770cWO0UWIusZl9if4oATA19fX5hGIQQxXR9rbbB',
  's3bjTE8ooVK9RRkzBzDSrqLXXaHteQMocwxkglNBJh9JK7vsf7LfIGEiABEiABEiABErAQARuBiahhQECA4R6idgUKFFC/v3nzxi',
  'bCOHHiRGnbtm0sgQmBhtmXONvcfMwiBCbE6pQpU1QdI+ZY4h/mbgYFBSmxikHmMNRVQtShaQjCEOeHZ8+eXUUQkR6vV6+e+hlHPu',
  'oOcqTAERV1ZvYd7kixY00Iw/DwcHn8+LESlvYNTWhA0jWgWBtNTahF1bZ+/XrV5AM/9NB21GiuXbtW1bCau+rd9d1Cfy90hQRIgA',
  'RIgARIgATiJWAjMM3pX3N0DquYxRzmT0Kg6RR3/fr1JSIiQr0M1xBpdDTSCNeRJo6MjFSjjVCbqIWq/YxK7TkGlCPdjmgnIqmo8U',
  'R6GbWP+h2ItNpHHe13jhmbmLWpDX4i7a6jtY5I6UYj8zUIUj8/v1jzOM3pbzT4oFbVvtzAXd/j/RZ5AwmQAAmQAAmQAAlYiICNwI',
  'TYQ00jzDz+B5FI1GJqQ4e4udEFz+BZe8Mz9nMxIT4RDdXRRnRr4zOkkjHKyN4QDYQYRDTVkaGzHJFR/B+XoT4SdZKuGN6JGkxnA9',
  'zthaN5TT1eSX9m7qS3f7ervrviM+8hARIgARIgARIgAasQsBGYiAIiaqcNNY6ZMmVSdY46QodGneDgYPHw8DDuQ60k0sM6XY0II1',
  'LLiPQhZY7Tf2BIneNsb4guiFasb26mwfObN29WUUWISpyUg4YjiFGIWFzTkVLUaaLmEcIRqe74DEdXIuWOE3jM0UY8BzGdL18+5S',
  '863RFdjc9CQ0PV4HgtWiEWsWeMPzLXW4LNv/U9Pl94nQRIgARIgARIgASsRMBGYKLRBvWNzgxpc9RLOjtvG2IqOjpaiUOz4TOM/t',
  'EpdfyMz/Tv5nuxBgQgmnocGZ6LiYlRNZjumn4Hnsc6uonJnfV+/Pgh+OeKPwnhuzs+8hkSIAESIAESIAESSEwCscYU2Y/z0c6gax',
  'yjfXx8fBLTP76LBEiABEiABEiABEggiRFwOGgdNZJhYWHy+vVrNWKnePHiNiN4ktge6S4JkAAJkAAJkAAJkEAiEojzLPJE9IOvIg',
  'ESIAESIAESIAESSCYEKDCTyRfJbZAACZAACZAACZCAVQhQYFrlm6AfJEACJEACJEACJJBMCFBgJpMvktsgARIgARIgARIgAasQoM',
  'C0yjdBP0iABEiABEiABEggmRCgwEwmXyS3QQIkQAIkQAIkQAJWIUCBaZVvgn6QAAmQAAmQAAmQQDIhQIGZTL5IboMESIAESIAESI',
  'AErELgf+R8cNSoevtOAAAAAElFTkSuQmCC',
].join('');

const buildAccount = (overrides: Partial<AccountDto> & Pick<AccountDto, 'type'>): AccountDto => ({
  id: 'preview-account',
  userId: 'preview-user',
  isRoot: true,
  isAccredited: true,
  createdAt: new Date().toISOString(),
  ...overrides,
});

// Used when the offer hasn't been fetched yet (or has no price/minimum set) so the preview
// still renders something reasonable.
const DEFAULT_PREVIEW_QUANTITY = 1000;
const DEFAULT_PREVIEW_TOTAL_COST = 5000;

const buildOfferOrderPreviewInput = (
  account: AccountDto,
  quantity: number,
  totalCost: number,
): OfferOrderPreviewInput => ({
  quantity,
  totalCost,
  localeDate: todayLocaleDate(),
  account,
});

const buildOfferOrderPreviewInputForAccountType = (
  accountType: AccountTypeDto,
  quantity: number,
  totalCost: number,
): OfferOrderPreviewInput => {
  switch (accountType) {
    case AccountTypeDto.Individual:
      return buildOfferOrderPreviewInput(
        buildAccount({ type: AccountTypeDto.Individual, primaryAccountHolder: johnDoeFixture }),
        quantity,
        totalCost,
      );
    case AccountTypeDto.Joint:
      return buildOfferOrderPreviewInput(
        buildAccount({
          type: AccountTypeDto.Joint,
          primaryAccountHolder: johnDoeFixture,
          secondaryAccountHolder: janeDoeFixture,
        }),
        quantity,
        totalCost,
      );
    case AccountTypeDto.Ugma:
      return buildOfferOrderPreviewInput(
        buildAccount({
          type: AccountTypeDto.Ugma,
          isAccredited: false,
          primaryAccountHolder: johnDoeFixture,
          secondaryAccountHolder: janeDoeFixture,
        }),
        quantity,
        totalCost,
      );
    case AccountTypeDto.Retirement:
      return buildOfferOrderPreviewInput(
        buildAccount({
          type: AccountTypeDto.Retirement,
          retirementAccountType: RetirementAccountTypeDto.Traditional,
          primaryAccountHolder: johnDoeFixture,
        }),
        quantity,
        totalCost,
      );
    case AccountTypeDto.Entity:
      return buildOfferOrderPreviewInput(
        buildAccount({
          type: AccountTypeDto.Entity,
          entityAccountType: EntityAccountTypeDto.LlcCCorporation,
          primaryAccountHolder: johnDoeFixture,
          entityInformation: entityInformationFixture,
        }),
        quantity,
        totalCost,
      );
  }
};

const setTextField = (form: PDFForm, field: Extract<FormField, { type: 'text' }>): void => {
  try {
    form.getTextField(field.name).setText(String(field.value));
  } catch (error) {
    Logger.warn(`Failed to set text field "${field.name}"`, error);
  }
};

const setCheckboxField = (form: PDFForm, field: Extract<FormField, { type: 'checkbox' }>): void => {
  try {
    const checkbox = form.getCheckBox(field.name);

    if (field.value) {
      checkbox.check();
    } else {
      checkbox.uncheck();
    }
  } catch (error) {
    Logger.warn(`Failed to set checkbox field "${field.name}"`, error);
  }
};

// Signature fields are PDF button widgets, not text/checkbox fields - mirrors worker's
// PdfFormEditor.drawSignature, which draws the signature image directly onto the page at the
// button's widget position (rather than PDFButton.setImage) so the preview matches how the real,
// signed PDF renders.
const MAX_SIGNATURE_WIDTH = 250;
const MAX_SIGNATURE_HEIGHT = 25;

const setSignatureField = async (
  pdfDoc: PDFDocument,
  form: PDFForm,
  field: Extract<FormField, { type: 'signature' }>,
): Promise<void> => {
  try {
    const signatureImage = await pdfDoc.embedPng(field.value);
    const buttonField = form.getButton(field.name);
    const widgets = buttonField.acroField.getWidgets();

    if (!widgets.length) {
      Logger.warn(`Button field "${field.name}" has no widgets. Skipping signature.`);

      return;
    }

    const pages = pdfDoc.getPages();
    const pageRefMap = new Map(pages.map(page => [page.ref.toString(), page]));
    const imgDims = signatureImage.scale(1);
    const aspectRatio = imgDims.width / imgDims.height;

    widgets.forEach(widget => {
      const rect = widget.getRectangle();
      const pageRef = widget.P();
      const page = pageRef && pageRefMap.get(pageRef.toString());

      if (!page) {
        return;
      }

      let width = Math.min(rect.width, MAX_SIGNATURE_WIDTH);
      let height = Math.min(rect.height, MAX_SIGNATURE_HEIGHT);

      if (width / height > aspectRatio) {
        width = height * aspectRatio;
      } else {
        height = width / aspectRatio;
      }

      page.drawImage(signatureImage, { x: rect.x, y: rect.y, width, height });
    });
  } catch (error) {
    Logger.warn(`Failed to draw signature for button field "${field.name}"`, error);
  }
};

// Pdf-lib drops checkbox borders on flatten, so redraw them beforehand - mirrors
// worker's PdfFormEditor.drawCheckboxBorder so the preview looks like the real, signed PDF.
const drawCheckboxBorders = (form: PDFForm, pdfDoc: PDFDocument, fields: FormField[]): void => {
  const pages = pdfDoc.getPages();
  const pageRefMap = new Map(pages.map(page => [page.ref.toString(), page]));

  fields
    .filter((field): field is Extract<FormField, { type: 'checkbox' }> => field.type === 'checkbox')
    .forEach(({ name }) => {
      try {
        const checkbox = form.getCheckBox(name);
        const widgets = checkbox.acroField.getWidgets();

        widgets.forEach(widget => {
          const rect = widget.getRectangle();
          const pageRef = widget.P();
          const page = pageRef && pageRefMap.get(pageRef.toString());

          if (!page) {
            return;
          }

          page.drawRectangle({
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height,
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });
        });
      } catch (error) {
        Logger.warn(`Failed to draw border for checkbox "${name}"`, error);
      }
    });
};

const fillFormFields = async (pdfDoc: PDFDocument, fields: FormField[]): Promise<void> => {
  const form = pdfDoc.getForm();

  for (const field of fields) {
    if (field.type === 'text') {
      setTextField(form, field);
      continue;
    }

    if (field.type === 'checkbox') {
      setCheckboxField(form, field);
      continue;
    }

    if (field.type === 'signature') {
      await setSignatureField(pdfDoc, form, field);
    }
  }

  drawCheckboxBorders(form, pdfDoc, fields);
  form.flatten();
};

export const fillSubscriptionAgreement = async ({
  file,
  accountType,
  quantity = DEFAULT_PREVIEW_QUANTITY,
  totalCost = DEFAULT_PREVIEW_TOTAL_COST,
}: {
  file: File;
  accountType: AccountTypeDto;
  quantity?: number;
  totalCost?: number;
}): Promise<{ pdfBytes: Uint8Array; url: string }> => {
  const offerOrder = buildOfferOrderPreviewInputForAccountType(accountType, quantity, totalCost);

  const fields =
    accountType === AccountTypeDto.Entity
      ? mapEntityAccountFormFields(offerOrder)
      : mapNonEntityAccountFormFields(offerOrder);

  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);

  await fillFormFields(pdfDoc, fields);

  const pdfBytes = new Uint8Array(await pdfDoc.save());
  const url = URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }));

  return { pdfBytes, url };
};
